import useRequest from "@/lib/request/useRequest";
import { useMemo, useState, useCallback } from "react";
import { WaterRiskZones, WaterReceived } from "@/types/water";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { scaleTime } from "d3-scale";
import {
    Switch,
    RangeValue,
    DateValue,
    ButtonGroup,
    Button,
} from "@heroui/react";
import { useI18n } from "@/lib/i18n/useI18n";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    Area,
    Label,
    Line,
    Tooltip,
    Legend,
    ReferenceArea,
    ReferenceLine,
} from "recharts";
import dayjs from "dayjs";
import { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { Link } from "@tanstack/react-router";
import PrecipitationEdit from "./PrecipitationEdit";

function SoilWaterContent({
    fieldId,
    dateRange,
}: {
    fieldId: string | undefined;
    dateRange: RangeValue<DateValue> | null;
}) {
    const { useGetRequest } = useRequest();
    const { formatDateMMDDYYYY, formatUnit } = useI18n();
    const { t } = useTranslation();
    const waterContentQuery = useGetRequest<WaterRiskZones>({
        url: `fields/${fieldId}/water_risk_zones/`,
    });

    const waterReceivedQuery = useGetRequest<WaterReceived>({
        url: `fields/${fieldId}/water_received/`,
    });

    const savedWaterReceived = useGetRequest<
        { date: string; precipitation: number; irrigation: number }[]
    >({
        url: `fields/${fieldId}/estimated_water_received/`,
    });

    const today = useMemo(
        () => getTodayWithTimeTravel().toDate().getTime(),
        [],
    );

    const waterReceivedData = useMemo(() => {
        return sortArrayByKey(
            waterReceivedQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(
                            dateRange?.start?.toString(),
                        ) &&
                        dayjs(d.time).isSameOrBefore(
                            dateRange?.end?.toString(),
                        ),
                )
                .map((d) => {
                    const weather = savedWaterReceived.data?.find(
                        (w) =>
                            dayjs(w.date).format("YYYY-MM-DD") ===
                            dayjs(d.time).format("YYYY-MM-DD"),
                    );

                    if (
                        weather &&
                        Math.abs(weather.precipitation - d.precipitation) > 0.01
                    ) {
                        return {
                            ...d,
                            precipitation: weather?.precipitation,
                        };
                    }
                    return d;
                })
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [
        waterReceivedQuery.data?.data,
        dateRange?.start,
        dateRange?.end,
        savedWaterReceived.data,
    ]);

    const totalPrecipitation = useMemo(
        () =>
            roundTo(
                waterReceivedData.reduce(
                    (acc, curr) => acc + curr.precipitation,
                    0,
                ),
                2,
            ),
        [waterReceivedData],
    );
    const totalIrrigation = useMemo(
        () =>
            roundTo(
                waterReceivedData.reduce(
                    (acc, curr) => acc + curr.irrigation,
                    0,
                ),
                2,
            ),
        [waterReceivedData],
    );

    const waterReceivedLookup = useMemo(
        () =>
            new Map(
                waterReceivedData.map((d) => [
                    dayjs(d.time).format("YYYY-MM-DD"),
                    d,
                ]),
            ),
        [waterReceivedData],
    );

    const [showEditPrecipitationModal, setShowEditPrecipitationModal] =
        useState(false);

    const dataInRange = useMemo(() => {
        return (
            waterContentQuery.data?.data?.filter(
                (d) =>
                    dayjs(d.time).isSameOrAfter(dateRange?.start?.toString()) &&
                    dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
            ) ?? []
        );
    }, [waterContentQuery.data?.data, dateRange]);

    const { fieldQuery } = useFieldDashboard() ?? {};
    const fieldData = fieldQuery?.data;

    const availableWaterContentData = useMemo(() => {
        return dataInRange.map((d) => {
            const r = waterReceivedLookup.get(
                dayjs(d.time).format("YYYY-MM-DD"),
            );
            return {
                ...d,
                time: dayjs(d.time).toDate().getTime(),
                availableWaterContent: d.swc_est ? d.swc_est - d.wp : null,
                saturated: [d.fhc - d.wp, d.rtsml_t - d.wp],
                optimal: [d.rtsml_b - d.wp, d.rtsml_t - d.wp],
                pink: [0, d.rtsml_b - d.wp],
                fcLine: d.fhc - d.wp,
                irrigation: r?.irrigation ?? 0,
                precipitation: r?.precipitation ?? 0,
            };
        });
    }, [dataInRange, waterReceivedLookup]);

    const moistureSufficiencyData = useMemo(() => {
        const targetMin = fieldData?.target_moisture_min;
        const targetMax = fieldData?.target_moisture_max;
        return dataInRange.map((d) => {
            const suff = d.swc_est ? (d.swc_est / d.fhc) * 100 : null;
            const lowerBound =
                targetMin != null ? targetMin : (d.rtsml_b / d.fhc) * 100;
            const upperBound =
                targetMax != null ? targetMax : (d.rtsml_t / d.fhc) * 100;
            const r = waterReceivedLookup.get(
                dayjs(d.time).format("YYYY-MM-DD"),
            );
            return {
                ...d,
                time: dayjs(d.time).toDate().getTime(),
                red: lowerBound ? [0, 0.5 * lowerBound] : null,
                yellow: lowerBound ? [0.5 * lowerBound, lowerBound] : null,
                green: lowerBound ? [lowerBound, upperBound] : null,
                blue: [upperBound, 1000],
                suff,
                irrigation: r?.irrigation ?? 0,
                precipitation: r?.precipitation ?? 0,
            };
        });
    }, [
        dataInRange,
        fieldData?.target_moisture_min,
        fieldData?.target_moisture_max,
        waterReceivedLookup,
    ]);

    const [sufficiencyMin, sufficiencyMax] = useMemo(() => {
        return [
            0,
            Math.max(
                100,
                Math.max(
                    ...moistureSufficiencyData
                        .filter((d) => d.suff !== null)
                        .map((d) => d.suff as number),
                ),
            ),
        ];
    }, [moistureSufficiencyData]);

    const waterContentData = useMemo(() => {
        return sortArrayByKey(
            dataInRange.map((d) => {
                const r = waterReceivedLookup.get(
                    dayjs(d.time).format("YYYY-MM-DD"),
                );
                return {
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                    range: [d.swc_sig_under, d.swc_sig_over],
                    green: [d.rtsml_b, d.rtsml_t],
                    red: [0, d.rtsml_b],
                    blue: [d.rtsml_t, 1000],
                    gray: [0, d.wp],
                    irrigation: r?.irrigation ?? 0,
                    precipitation: r?.precipitation ?? 0,
                };
            }) ?? [],
            "time",
        );
    }, [dataInRange, waterReceivedLookup]);

    const summaryData = useMemo(() => {
        if (!dataInRange.length) return null;

        const findNearest = (targetMs: number) =>
            dataInRange.reduce((nearest, d) => {
                const dMs = dayjs(d.time).valueOf();
                const nearestMs = dayjs(nearest.time).valueOf();
                return Math.abs(dMs - targetMs) < Math.abs(nearestMs - targetMs)
                    ? d
                    : nearest;
            });

        const targetMin = fieldData?.target_moisture_min;
        const targetMax = fieldData?.target_moisture_max;

        const moistureStatusClass = (d: (typeof dataInRange)[0]) => {
            if (d.swc_est == null || !d.fhc) return "";
            const upper =
                targetMax != null ? (d.fhc * targetMax) / 100 : d.rtsml_t;
            const lower =
                targetMin != null ? (d.fhc * targetMin) / 100 : d.rtsml_b;
            if (d.swc_est >= upper) return "bg-blue-50 text-blue-700";
            if (d.swc_est >= lower) return "bg-green-50 text-green-700";
            return "bg-yellow-50 text-yellow-700";
        };

        const compute = (d: (typeof dataInRange)[0]) => {
            const suff =
                d.swc_est != null && d.fhc ? (d.swc_est / d.fhc) * 100 : null;
            const deficitToFC = d.swc_est != null ? d.fhc - d.swc_est : null;
            const deficitToOptimal =
                d.swc_est != null ? d.rtsml_t - d.swc_est : null;
            return {
                suff,
                deficitToFC,
                deficitToOptimal,
                cellClass: moistureStatusClass(d),
            };
        };

        const todayMs = getTodayWithTimeTravel().valueOf();
        return {
            current: compute(findNearest(todayMs)),
            threeDayForecast: compute(
                findNearest(getTodayWithTimeTravel().add(3, "day").valueOf()),
            ),
            sevenDayForecast: compute(
                findNearest(getTodayWithTimeTravel().add(7, "day").valueOf()),
            ),
        };
    }, [
        dataInRange,
        fieldData?.target_moisture_min,
        fieldData?.target_moisture_max,
    ]);

    const { setSelectedDate } = useFieldDashboard() ?? {};
    const [showSWCRanges, setShowSWCRanges] = useState(false);

    const maxValue = waterContentData.length
        ? Math.max(...waterContentData.map((d) => d.time as number))
        : today;
    const minValue = waterContentData.length
        ? Math.min(...waterContentData.map((d) => d.time as number))
        : today;
    const timeScale = scaleTime().domain([minValue, maxValue]);

    const domain = timeScale.domain().map((d) => d.getTime());
    const ticks = timeScale.ticks(5).map((d) => d.getTime());

    const forecastReferenceArea =
        domain[1] > today ? (
            <ReferenceArea
                yAxisId="left"
                x1={today}
                x2={domain[1]}
                stroke="#09426A"
                strokeOpacity={0.5}
                fill="#09426A"
                fillOpacity={0.05}
            >
                <Label
                    value="Forecasted"
                    position="top"
                    fontSize={10}
                    dx={-14}
                />
            </ReferenceArea>
        ) : null;

    const [fieldCapacityRef, wiltingPointRef] = useMemo(() => {
        if (!waterContentData.length) return [null, null];
        const last = waterContentData[waterContentData.length - 1];
        return [last.fhc ?? null, last.wp ?? null];
    }, [waterContentData]);

    const [minSoilWaterContent, maxSoilWaterContent] = useMemo(() => {
        return [
            Math.min(
                ...waterContentData
                    .filter((d) => d.wp !== null)
                    .map((d) => d.wp as number),
            ) - 1,
            Math.max(
                ...waterContentData
                    .filter((d) => d.swc_sig_over !== null)
                    .map((d) => d.swc_sig_over as number),
            ),
        ];
    }, [waterContentData]);

    const onChartClick: CategoricalChartFunc = useCallback(
        (data) => {
            const date = dayjs(data.activePayload?.[0]?.payload?.time).format(
                "YYYY-MM-DD",
            );
            if (date) {
                setSelectedDate?.(date);
            }
        },
        [setSelectedDate],
    );

    const [graphType, setGraphType] = useState<
        "waterContent" | "availableWaterContent" | "moistureSufficiency"
    >("availableWaterContent");

    return (
        <div>
            <div className="font-semibold">Water Availability</div>
            {summaryData && (
                <>
                    <p className="text-xs text-default-400 text-end">
                        * Forecast values include projected precipitation.
                    </p>
                    <table className="w-full text-[10px] border-collapse mb-1">
                        <thead>
                            <tr className="bg-default-100">
                                <th className="p-1 border border-default-200 text-left font-semibold" />
                                <th className="p-1 border border-default-200 text-center font-semibold">
                                    Current
                                </th>
                                <th className="p-1 border border-default-200 text-center font-semibold">
                                    +3 Day Forecast
                                </th>
                                <th className="p-1 border border-default-200 text-center font-semibold">
                                    +7 Day Forecast
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="p-1 border border-default-200">
                                    Moisture Sufficiency
                                </td>
                                {[
                                    summaryData.current,
                                    summaryData.threeDayForecast,
                                    summaryData.sevenDayForecast,
                                ].map((d, i) => (
                                    <td
                                        key={i}
                                        className={`p-1 border border-default-200 text-center ${d.cellClass}`}
                                    >
                                        {d.suff != null
                                            ? `${roundTo(d.suff, 1)}%`
                                            : "—"}
                                    </td>
                                ))}
                            </tr>
                            <tr>
                                <td className="p-1 border border-default-200">
                                    Deficit to FC
                                </td>
                                {[
                                    summaryData.current,
                                    summaryData.threeDayForecast,
                                    summaryData.sevenDayForecast,
                                ].map((d, i) => (
                                    <td
                                        key={i}
                                        className={`p-1 border border-default-200 text-center ${d.cellClass}`}
                                    >
                                        {d.deficitToFC != null
                                            ? `${roundTo(d.deficitToFC, 2)} ${formatUnit("in", "mm")}`
                                            : "—"}
                                    </td>
                                ))}
                            </tr>
                            <tr>
                                <td className="p-1 border border-default-200">
                                    Deficit to Top of Optimal
                                </td>
                                {[
                                    summaryData.current,
                                    summaryData.threeDayForecast,
                                    summaryData.sevenDayForecast,
                                ].map((d, i) => (
                                    <td
                                        key={i}
                                        className={`p-1 border border-default-200 text-center ${d.cellClass}`}
                                    >
                                        {d.deficitToOptimal != null
                                            ? `${roundTo(d.deficitToOptimal, 2)} ${formatUnit("in", "mm")}`
                                            : "—"}
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </>
            )}
            <div className="flex flex-row items-center gap-2 justify-between">
                <div>
                    <ButtonGroup size="sm">
                        <Button
                            color={
                                graphType === "availableWaterContent"
                                    ? "primary"
                                    : "default"
                            }
                            onPress={() =>
                                setGraphType("availableWaterContent")
                            }
                        >
                            Available Water
                        </Button>

                        <Button
                            color={
                                graphType === "moistureSufficiency"
                                    ? "primary"
                                    : "default"
                            }
                            onPress={() => setGraphType("moistureSufficiency")}
                        >
                            Moisture Sufficiency
                        </Button>
                        <Button
                            color={
                                graphType === "waterContent"
                                    ? "primary"
                                    : "default"
                            }
                            onPress={() => setGraphType("waterContent")}
                        >
                            Total Water
                        </Button>
                    </ButtonGroup>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <Button
                        size="sm"
                        radius="sm"
                        color="primary"
                        as={Link}
                        to={`/dashboard/fields/${fieldId}/operations`}
                        target="_blank"
                    >
                        Irrigation: {totalIrrigation} {formatUnit("in", "mm")}
                    </Button>
                    <Button
                        size="sm"
                        radius="sm"
                        onPress={() => setShowEditPrecipitationModal(true)}
                        color="primary"
                    >
                        Precip: {totalPrecipitation} {formatUnit("in", "mm")}
                    </Button>
                    {graphType === "waterContent" && (
                        <>
                            <div className="text-sm">Uncertainty</div>
                            <Switch
                                size="sm"
                                aria-label="Uncertainty Threshold"
                                isSelected={showSWCRanges}
                                onValueChange={setShowSWCRanges}
                            />
                        </>
                    )}
                </div>
            </div>
            {waterContentData.length ? (
                <>
                    {graphType === "waterContent" && (
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart
                                data={waterContentData}
                                syncId="water"
                                onClick={onChartClick}
                                style={{ cursor: "pointer" }}
                            >
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    yAxisId="left"
                                    fontSize={12}
                                    label={{
                                        value: t(
                                            "chartLabels.fieldSoilWaterContent",
                                            {
                                                ns: "insights",
                                            },
                                        ),
                                        angle: -90,
                                        dx: -20,
                                    }}
                                    domain={[
                                        Math.floor(minSoilWaterContent),
                                        Math.ceil(maxSoilWaterContent),
                                    ]}
                                    allowDataOverflow
                                    type="number"
                                    scale="linear"
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    fontSize={12}
                                    label={{
                                        value: formatUnit("in", "mm"),
                                        angle: 90,
                                        dx: 20,
                                    }}
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="red"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="red"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.nearingDepletion", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="red"
                                    legendType="rect"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="blue"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="blue"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.nearingSaturation", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="blue"
                                    legendType="rect"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="green"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="green"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.optimal", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="green"
                                    legendType="rect"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="gray"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="gray"
                                    fillOpacity={0.1}
                                    name="Wilting Point"
                                    tooltipType="none"
                                    stroke="gray"
                                    legendType="rect"
                                />
                                {showSWCRanges && (
                                    <Area
                                        yAxisId="left"
                                        dataKey="range"
                                        dot={false}
                                        strokeWidth={0}
                                        fill="black"
                                        fillOpacity={0.2}
                                        tooltipType="none"
                                        legendType="none"
                                    />
                                )}
                                <Line
                                    yAxisId="left"
                                    dataKey="swc_est"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={3}
                                    name={t("chartLabels.soilWaterContent", {
                                        ns: "insights",
                                    })}
                                />
                                {fieldCapacityRef != null && (
                                    <ReferenceLine
                                        yAxisId="left"
                                        y={fieldCapacityRef}
                                        stroke="black"
                                        strokeDasharray="4 4"
                                        strokeOpacity={0.5}
                                        label={{
                                            value: "FC",
                                            position: "insideTopRight",
                                            fontSize: 10,
                                        }}
                                    />
                                )}
                                {wiltingPointRef != null && (
                                    <ReferenceLine
                                        yAxisId="left"
                                        y={wiltingPointRef}
                                        stroke="gray"
                                        strokeDasharray="4 4"
                                        strokeOpacity={0.5}
                                        label={{
                                            value: "WP",
                                            position: "insideTopRight",
                                            fontSize: 10,
                                        }}
                                    />
                                )}
                                <Bar
                                    yAxisId="right"
                                    dataKey="irrigation"
                                    fill="#2d7db3"
                                    barSize={10}
                                    name={t("chartLabels.irrigation", {
                                        ns: "insights",
                                    })}
                                >
                                    {waterContentData.map((entry, index) => (
                                        <Cell
                                            key={index}
                                            fill="#2d7db3"
                                            opacity={
                                                entry.time > today ? 0.4 : 1
                                            }
                                        />
                                    ))}
                                </Bar>
                                <Bar
                                    yAxisId="right"
                                    dataKey="precipitation"
                                    fill="#8ab4d4"
                                    barSize={10}
                                    name={t("chartLabels.precipitation", {
                                        ns: "insights",
                                    })}
                                    onClick={() =>
                                        setShowEditPrecipitationModal(true)
                                    }
                                >
                                    {waterContentData.map((entry, index) => (
                                        <Cell
                                            key={index}
                                            fill="#8ab4d4"
                                            opacity={
                                                entry.time > today ? 0.4 : 1
                                            }
                                        />
                                    ))}
                                </Bar>
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                                {forecastReferenceArea}
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                    {graphType == "availableWaterContent" && (
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart
                                data={availableWaterContentData}
                                syncId="water"
                                style={{ cursor: "pointer" }}
                            >
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    yAxisId="left"
                                    fontSize={12}
                                    label={{
                                        value: "Available Water Content",
                                        angle: -90,
                                        dx: -20,
                                    }}
                                    allowDataOverflow
                                    type="number"
                                    scale="linear"
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    fontSize={12}
                                    label={{
                                        value: formatUnit("in", "mm"),
                                        angle: 90,
                                        dx: 20,
                                    }}
                                />
                                <Line
                                    yAxisId="left"
                                    dataKey="fcLine"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    name="Field Capacity"
                                    tooltipType="none"
                                    legendType="plainline"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="saturated"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="blue"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    legendType="rect"
                                    stroke="blue"
                                    name="Saturated"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="optimal"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="green"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    legendType="rect"
                                    stroke="green"
                                    name="Optimal"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="pink"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="red"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    legendType="rect"
                                    stroke="red"
                                    name="Depletion"
                                />
                                <Line
                                    yAxisId="left"
                                    dataKey="availableWaterContent"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={3}
                                    name="Available Water Content"
                                />
                                <Bar
                                    yAxisId="right"
                                    dataKey="irrigation"
                                    fill="#2d7db3"
                                    barSize={10}
                                    name={t("chartLabels.irrigation", {
                                        ns: "insights",
                                    })}
                                >
                                    {availableWaterContentData.map(
                                        (entry, index) => (
                                            <Cell
                                                key={index}
                                                fill="#2d7db3"
                                                opacity={
                                                    entry.time > today ? 0.4 : 1
                                                }
                                            />
                                        ),
                                    )}
                                </Bar>
                                <Bar
                                    yAxisId="right"
                                    dataKey="precipitation"
                                    fill="#8ab4d4"
                                    barSize={10}
                                    name={t("chartLabels.precipitation", {
                                        ns: "insights",
                                    })}
                                    onClick={() =>
                                        setShowEditPrecipitationModal(true)
                                    }
                                >
                                    {availableWaterContentData.map(
                                        (entry, index) => (
                                            <Cell
                                                key={index}
                                                fill="#8ab4d4"
                                                opacity={
                                                    entry.time > today ? 0.4 : 1
                                                }
                                            />
                                        ),
                                    )}
                                </Bar>
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                                {forecastReferenceArea}
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                    {graphType === "moistureSufficiency" && (
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart
                                data={moistureSufficiencyData}
                                syncId="water"
                                style={{ cursor: "pointer" }}
                            >
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    yAxisId="left"
                                    fontSize={12}
                                    label={{
                                        value: "% of Field Capacity",
                                        angle: -90,
                                        dx: -20,
                                    }}
                                    allowDataOverflow
                                    type="number"
                                    scale="linear"
                                    domain={[sufficiencyMin, sufficiencyMax]}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    fontSize={12}
                                    label={{
                                        value: formatUnit("in", "mm"),
                                        angle: 90,
                                        dx: 20,
                                    }}
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="red"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="red"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    name="Depleted"
                                    stroke="red"
                                    legendType="rect"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="yellow"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="yellow"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    name="Approaching Deficiency"
                                    stroke="#b59d19"
                                    legendType="rect"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="green"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="green"
                                    stroke="green"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    name="Sufficient"
                                    legendType="rect"
                                />
                                <Area
                                    yAxisId="left"
                                    dataKey="blue"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="blue"
                                    fillOpacity={0.1}
                                    tooltipType="none"
                                    name="Saturated"
                                    stroke="blue"
                                    legendType="rect"
                                />
                                <Line
                                    yAxisId="left"
                                    dataKey="suff"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={2}
                                    name="Moisture Sufficiency"
                                />
                                <Bar
                                    yAxisId="right"
                                    dataKey="irrigation"
                                    fill="#2d7db3"
                                    barSize={10}
                                    name={t("chartLabels.irrigation", {
                                        ns: "insights",
                                    })}
                                >
                                    {moistureSufficiencyData.map(
                                        (entry, index) => (
                                            <Cell
                                                key={index}
                                                fill="#2d7db3"
                                                opacity={
                                                    entry.time > today ? 0.4 : 1
                                                }
                                            />
                                        ),
                                    )}
                                </Bar>
                                <Bar
                                    yAxisId="right"
                                    dataKey="precipitation"
                                    fill="#8ab4d4"
                                    barSize={10}
                                    name={t("chartLabels.precipitation", {
                                        ns: "insights",
                                    })}
                                    onClick={() =>
                                        setShowEditPrecipitationModal(true)
                                    }
                                >
                                    {moistureSufficiencyData.map(
                                        (entry, index) => (
                                            <Cell
                                                key={index}
                                                fill="#8ab4d4"
                                                opacity={
                                                    entry.time > today ? 0.4 : 1
                                                }
                                            />
                                        ),
                                    )}
                                </Bar>
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} %`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                                {forecastReferenceArea}
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                </>
            ) : (
                <div className="text-sm italic text-center mt-4">
                    No data available
                </div>
            )}

            {showEditPrecipitationModal && (
                <PrecipitationEdit
                    precipitationData={waterReceivedData.map((d) => ({
                        field: fieldId!,
                        date: dayjs(d.time).format("YYYY-MM-DD"),
                        precipitation: roundTo(d.precipitation, 5),
                    }))}
                    onClose={() => {
                        waterReceivedQuery.refetch();
                        savedWaterReceived.refetch();
                        setShowEditPrecipitationModal(false);
                    }}
                />
            )}
        </div>
    );
}

export default SoilWaterContent;
